import { useState, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import ExerciseService from '../../services/exerciseService';
import { CardioSessionService } from '../../services/cardioSessionService';
import { WorkoutPlanService } from '../../services/workoutPlanService';
import { GymService } from '../../services/gymService';
import { TrainingProgramService } from '../../services/trainingProgramService';
import { ExerciseGroupService } from '../../services/exerciseGroupService';
import type { ExerciseGroup, GroupAnalysisResponse } from '../../services/exerciseGroupService';
import type { ExerciseRecordsResponse, Gym, TrainingLabel, TrainingProgram } from '../../services/types';
import type {
  ChartDataPoint,
  ExerciseOption,
  LastExecutionRow,
  LastSessionDetail,
  WorkoutProgressResponse,
  ExerciseProgressResponse,
  ProgramExercise,
  BatchSuccessRateEntry,
  CardioProgressSession,
} from './types';
import type { ExerciseStatsRow } from '../../services/types';

const WORKOUT_SESSION_UPDATED_EVENT = 'workout-session-updated';

export function useProgressData() {
  const [progressTab, setProgressTab] = useState<'strength' | 'cardio'>('strength');
  const [workoutProgress, setWorkoutProgress] = useState<WorkoutProgressResponse | null>(null);
  const [exerciseProgress, setExerciseProgress] = useState<ExerciseProgressResponse | null>(null);
  const [cardioProgress, setCardioProgress] = useState<CardioProgressSession[]>([]);
  const [exerciseOptions, setExerciseOptions] = useState<ExerciseOption[]>([]);
  const [selectedExerciseId, setSelectedExerciseId] = useState<number | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [selectedProgramDay, setSelectedProgramDay] = useState<number | null>(null);
  const [availableProgramDays, setAvailableProgramDays] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'exercise' | 'group'>('all');
  const [groups, setGroups] = useState<ExerciseGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [groupAnalysis, setGroupAnalysis] = useState<GroupAnalysisResponse | null>(null);
  const [groupAnalysisLoading, setGroupAnalysisLoading] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState<number | null>(null);
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState<string | null>(null);
  const [selectedTrainingLabel, setSelectedTrainingLabel] = useState<TrainingLabel | null>(null);
  const [selectedGymId, setSelectedGymId] = useState<number | null>(null);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [successRates, setSuccessRates] = useState<BatchSuccessRateEntry[]>([]);
  const [exerciseStats, setExerciseStats] = useState<ExerciseStatsRow[]>([]);
  const [exerciseStatsLoading, setExerciseStatsLoading] = useState(false);
  const [exerciseRecords, setExerciseRecords] = useState<ExerciseRecordsResponse | null>(null);
  const [exerciseRecordsLoading, setExerciseRecordsLoading] = useState(false);
  const [exerciseRecordsError, setExerciseRecordsError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState<string>(() => dayjs().subtract(1, 'month').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState<string>(() => dayjs().format('YYYY-MM-DD'));

  const loadStrengthFilterOptions = useCallback(async (programId?: number | null) => {
    const resolvedProgramId = programId ?? undefined;
    const [days, exercisesWithHistory] = await Promise.all([
      ExerciseService.getAvailableProgramDays(resolvedProgramId),
      ExerciseService.getExercisesWithHistory(resolvedProgramId),
    ]);

    setAvailableProgramDays(days);
    setExerciseOptions(exercisesWithHistory);

    return { days, exercisesWithHistory };
  }, []);

  // Initial load
  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      try {
        const [programList, cardioData, availableGyms, groupList] = await Promise.all([
          TrainingProgramService.getAll().catch(() => []),
          CardioSessionService.getProgress(dateFrom, dateTo).catch(() => ({ sessions: [] })),
          GymService.getWorkoutGymSelector()
            .then((response) => response.gyms)
            .catch(() => GymService.getAll().catch(() => [])),
          ExerciseGroupService.listGroups().catch(() => []),
        ]);
        setGroups(groupList);
        const initialProgramId = programList.find((program) => program.active)?.id ?? null;
        const [{ days }, batchRates] = await Promise.all([
          loadStrengthFilterOptions(initialProgramId),
          WorkoutPlanService.getBatchSuccessRates(dateFrom, dateTo, undefined, initialProgramId ?? undefined).catch(() => ({ rates: [] })),
        ]);

        setPrograms(programList);
        setSelectedProgramId(initialProgramId);
        setCardioProgress(cardioData.sessions);
        setSuccessRates(batchRates.rates);
        setGyms(availableGyms);

        if (days.length > 0) {
          const firstDay = days[0];
          setSelectedProgramDay(firstDay);
          const filtered = await ExerciseService.getWorkoutProgress(
            firstDay,
            dateFrom,
            dateTo,
            selectedGymId ?? undefined,
            selectedTrainingLabel,
            initialProgramId ?? undefined
          );
          setWorkoutProgress(filtered);
        }
      } catch (error) {
        console.error('Failed to load progress data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProgressForDay = async (
    day: number | null,
    fromDate?: string,
    toDate?: string,
    gymId?: number | null,
    trainingLabel?: TrainingLabel | null,
    programId?: number | null,
  ) => {
    setLoading(true);
    const df = fromDate ?? dateFrom;
    const dt = toDate ?? dateTo;
    const gid = gymId ?? selectedGymId;
    const label = trainingLabel ?? selectedTrainingLabel;
    const pid = programId ?? selectedProgramId;
    try {
      if (viewMode === 'exercise' && selectedExerciseId) {
        const exerciseData = await ExerciseService.getExerciseProgress(selectedExerciseId, day ?? undefined, undefined, df, dt, gid ?? undefined, label, pid ?? undefined);
        setExerciseProgress(exerciseData);
      } else if (day !== null) {
        const progressData = await ExerciseService.getWorkoutProgress(day, df, dt, gid ?? undefined, label, pid ?? undefined);
        setWorkoutProgress(progressData);
      } else {
        setWorkoutProgress(null);
      }
    } catch (error) {
      console.error('Failed to load progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExerciseProgress = async (
    exerciseId: number,
    dayOverride?: number | null,
    fromDate?: string,
    toDate?: string,
    gymId?: number | null,
    trainingLabel?: TrainingLabel | null,
    programId?: number | null,
  ) => {
    setLoading(true);
    try {
      const day = dayOverride !== undefined ? dayOverride : selectedProgramDay;
      const df = fromDate ?? dateFrom;
      const dt = toDate ?? dateTo;
      const gid = gymId ?? selectedGymId;
      const label = trainingLabel ?? selectedTrainingLabel;
      const pid = programId ?? selectedProgramId;
      const data = await ExerciseService.getExerciseProgress(exerciseId, day ?? undefined, undefined, df, dt, gid ?? undefined, label, pid ?? undefined);
      setExerciseProgress(data);
      setSelectedExerciseId(exerciseId);
      setViewMode('exercise');
    } catch (error) {
      console.error('Failed to load exercise progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedExerciseData = useCallback(async () => {
    if (!selectedExerciseId) {
      setExerciseRecords(null);
      setExerciseRecordsError(null);
      return;
    }

    setExerciseRecordsLoading(true);
    setExerciseRecordsError(null);
    try {
      const records = await ExerciseService.getExerciseRecords(selectedExerciseId);
      setExerciseRecords(records);
    } catch (error) {
      console.error('Failed to load exercise records:', error);
      setExerciseRecords(null);

      const parsed = ExerciseService.parseApiError(error);
      if (parsed.status === 401) {
        setExerciseRecordsError('Трябва да влезеш в профила си отново.');
      } else if (parsed.status === 403) {
        setExerciseRecordsError('Нямаш достъп до рекордите за това упражнение.');
      } else if (parsed.status === 404) {
        setExerciseRecordsError('Упражнението не беше намерено.');
      } else {
        setExerciseRecordsError('Неуспешно зареждане на рекордите.');
      }
    } finally {
      setExerciseRecordsLoading(false);
    }
  }, [selectedExerciseId]);

  const reloadCurrentProgressView = useCallback(async () => {
    if (progressTab !== 'strength') {
      try {
        const [cardioData, batchRates] = await Promise.all([
          CardioSessionService.getProgress(dateFrom, dateTo).catch(() => ({ sessions: [] })),
          WorkoutPlanService.getBatchSuccessRates(dateFrom, dateTo, undefined, selectedProgramId ?? undefined).catch(() => ({ rates: [] })),
        ]);
        setCardioProgress(cardioData.sessions);
        setSuccessRates(batchRates.rates);
      } catch (error) {
        console.error('Failed to refresh cardio progress data:', error);
      }

      return;
    }

    try {
      if (selectedMuscleGroup) {
        const data = await ExerciseService.getWorkoutProgressByMuscleGroup(
          selectedMuscleGroup,
          dateFrom,
          dateTo,
          selectedGymId ?? undefined,
          selectedTrainingLabel,
          selectedProgramId ?? undefined
        );
        setWorkoutProgress(data);
      } else if (viewMode === 'exercise' && selectedExerciseId) {
        const data = await ExerciseService.getExerciseProgress(
          selectedExerciseId,
          selectedProgramDay ?? undefined,
          undefined,
          dateFrom,
          dateTo,
          selectedGymId ?? undefined,
          selectedTrainingLabel,
          selectedProgramId ?? undefined
        );
        setExerciseProgress(data);
      } else if (selectedProgramDay !== null) {
        const data = await ExerciseService.getWorkoutProgress(
          selectedProgramDay,
          dateFrom,
          dateTo,
          selectedGymId ?? undefined,
          selectedTrainingLabel,
          selectedProgramId ?? undefined
        );
        setWorkoutProgress(data);
      } else {
        setWorkoutProgress(null);
      }

      const [cardioData, batchRates] = await Promise.all([
        CardioSessionService.getProgress(dateFrom, dateTo).catch(() => ({ sessions: [] })),
        WorkoutPlanService.getBatchSuccessRates(dateFrom, dateTo, undefined, selectedProgramId ?? undefined).catch(() => ({ rates: [] })),
      ]);
      setCardioProgress(cardioData.sessions);
      setSuccessRates(batchRates.rates);
    } catch (error) {
      console.error('Failed to refresh progress data:', error);
    }
  }, [
    progressTab,
    dateFrom,
    dateTo,
    selectedMuscleGroup,
    selectedGymId,
    selectedProgramId,
    selectedTrainingLabel,
    viewMode,
    selectedExerciseId,
    selectedProgramDay,
  ]);

  // Group exercises: base exercises shown normally, variations grouped under their parent
  const groupedExerciseOptions = useMemo(() => {
    const baseExercises: ExerciseOption[] = [];
    const variationsByParent: { [parentId: number]: ExerciseOption[] } = {};

    exerciseOptions.forEach(ex => {
      if (ex.parentExerciseId) {
        if (!variationsByParent[ex.parentExerciseId]) {
          variationsByParent[ex.parentExerciseId] = [];
        }
        variationsByParent[ex.parentExerciseId].push(ex);
      } else {
        baseExercises.push(ex);
      }
    });

    const result: { exercise: ExerciseOption; isVariation: boolean }[] = [];
    baseExercises.forEach(base => {
      result.push({ exercise: base, isVariation: false });
      const variations = variationsByParent[base.id];
      if (variations) {
        variations.forEach(v => result.push({ exercise: v, isVariation: true }));
      }
    });

    exerciseOptions.forEach(ex => {
      if (ex.parentExerciseId && !baseExercises.find(b => b.id === ex.parentExerciseId)) {
        if (!result.find(r => r.exercise.id === ex.id)) {
          result.push({ exercise: ex, isVariation: true });
        }
      }
    });

    return result;
  }, [exerciseOptions]);

  const variationOptions = useMemo(() => {
    if (!selectedExerciseId) return [];
    const selected = exerciseOptions.find(e => e.id === selectedExerciseId);
    if (!selected) return [];
    const baseId = selected.parentExerciseId ?? selected.id;
    const family = exerciseOptions.filter(e => e.id === baseId || e.parentExerciseId === baseId);
    if (family.length <= 1) return [];
    return family;
  }, [selectedExerciseId, exerciseOptions]);

  // Event handlers
  const handleExerciseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedVariation(null);
    setSelectedMuscleGroup(null);
    if (value === '') {
      setSelectedExerciseId(null);
      setExerciseProgress(null);
      if (selectedProgramDay !== null) {
        setViewMode('all');
        loadProgressForDay(selectedProgramDay, undefined, undefined, selectedGymId, selectedTrainingLabel, selectedProgramId);
      } else {
        setViewMode('all');
      }
    } else {
      const exerciseId = parseInt(value);
      if (exerciseId) loadExerciseProgress(exerciseId, undefined, undefined, undefined, selectedGymId, selectedTrainingLabel, selectedProgramId);
    }
  };

  const handleProgramDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedMuscleGroup(null);
    if (value === '') {
      setSelectedProgramDay(null);
      if (selectedExerciseId) loadExerciseProgress(selectedExerciseId, null, undefined, undefined, selectedGymId, selectedTrainingLabel, selectedProgramId);
    } else {
      const day = parseInt(value);
      if (!isNaN(day)) {
        setSelectedProgramDay(day);
        loadProgressForDay(day, undefined, undefined, selectedGymId, selectedTrainingLabel, selectedProgramId);
      }
    }
  };

  const handleProgramChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const nextProgramId = value === '' ? null : parseInt(value, 10);

    setSelectedProgramId(nextProgramId);
    setSelectedExerciseId(null);
    setExerciseProgress(null);
    setSelectedVariation(null);
    setSelectedMuscleGroup(null);
    setViewMode('all');
    setLoading(true);

    try {
      const [{ days }, batchRates] = await Promise.all([
        loadStrengthFilterOptions(nextProgramId),
        WorkoutPlanService.getBatchSuccessRates(dateFrom, dateTo, undefined, nextProgramId ?? undefined).catch(() => ({ rates: [] })),
      ]);

      const nextDay = days[0] ?? null;
      setSelectedProgramDay(nextDay);
      setSuccessRates(batchRates.rates);

      if (nextDay !== null) {
        const data = await ExerciseService.getWorkoutProgress(
          nextDay,
          dateFrom,
          dateTo,
          selectedGymId ?? undefined,
          selectedTrainingLabel,
          nextProgramId ?? undefined
        );
        setWorkoutProgress(data);
      } else {
        setWorkoutProgress(null);
      }
    } catch (error) {
      console.error('Failed to reload progress data for program filter:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVariationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      setSelectedVariation(null);
      if (selectedExerciseId) loadExerciseProgress(selectedExerciseId, undefined, undefined, undefined, selectedGymId, selectedTrainingLabel, selectedProgramId);
    } else {
      const variationId = parseInt(value);
      if (variationId) {
        setSelectedVariation(variationId);
        loadExerciseProgress(variationId, undefined, undefined, undefined, selectedGymId, selectedTrainingLabel, selectedProgramId);
      }
    }
  };

  const handleMuscleGroupChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      setSelectedMuscleGroup(null);
      setSelectedExerciseId(null);
      setExerciseProgress(null);
      setSelectedVariation(null);
      if (selectedProgramDay !== null) {
        setViewMode('all');
        loadProgressForDay(selectedProgramDay, undefined, undefined, selectedGymId, selectedTrainingLabel, selectedProgramId);
      } else {
        setViewMode('all');
        setWorkoutProgress(null);
      }
    } else {
      setSelectedMuscleGroup(value);
      setSelectedExerciseId(null);
      setExerciseProgress(null);
      setSelectedVariation(null);
      setSelectedProgramDay(null);
      setViewMode('all');
      setLoading(true);
      try {
        const data = await ExerciseService.getWorkoutProgressByMuscleGroup(value, dateFrom, dateTo, selectedGymId ?? undefined, selectedTrainingLabel, selectedProgramId ?? undefined);
        setWorkoutProgress(data);
      } catch (error) {
        console.error('Failed to load muscle group progress:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDateChange = async (newFrom: string, newTo: string) => {
    setDateFrom(newFrom);
    setDateTo(newTo);
    setLoading(true);
    try {
      if (progressTab === 'strength') {
        if (selectedMuscleGroup) {
          const data = await ExerciseService.getWorkoutProgressByMuscleGroup(selectedMuscleGroup, newFrom, newTo, selectedGymId ?? undefined, selectedTrainingLabel, selectedProgramId ?? undefined);
          setWorkoutProgress(data);
        } else if (viewMode === 'exercise' && selectedExerciseId) {
          const data = await ExerciseService.getExerciseProgress(selectedExerciseId, selectedProgramDay ?? undefined, undefined, newFrom, newTo, selectedGymId ?? undefined, selectedTrainingLabel, selectedProgramId ?? undefined);
          setExerciseProgress(data);
        } else if (selectedProgramDay !== null) {
          const data = await ExerciseService.getWorkoutProgress(selectedProgramDay, newFrom, newTo, selectedGymId ?? undefined, selectedTrainingLabel, selectedProgramId ?? undefined);
          setWorkoutProgress(data);
        }
      }
      const [cardioData, batchRates] = await Promise.all([
        CardioSessionService.getProgress(newFrom, newTo).catch(() => ({ sessions: [] })),
        WorkoutPlanService.getBatchSuccessRates(newFrom, newTo, undefined, selectedProgramId ?? undefined).catch(() => ({ rates: [] })),
      ]);
      setCardioProgress(cardioData.sessions);
      setSuccessRates(batchRates.rates);
    } catch (error) {
      console.error('Failed to reload data with new date range:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGymChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const nextGymId = value === '' ? null : parseInt(value, 10);
    setSelectedGymId(nextGymId);

    if (progressTab !== 'strength') {
      return;
    }

    setLoading(true);
    try {
      if (selectedMuscleGroup) {
        const data = await ExerciseService.getWorkoutProgressByMuscleGroup(selectedMuscleGroup, dateFrom, dateTo, nextGymId ?? undefined, selectedTrainingLabel, selectedProgramId ?? undefined);
        setWorkoutProgress(data);
      } else if (viewMode === 'exercise' && selectedExerciseId) {
        const data = await ExerciseService.getExerciseProgress(
          selectedExerciseId,
          selectedProgramDay ?? undefined,
          undefined,
          dateFrom,
          dateTo,
          nextGymId ?? undefined,
          selectedTrainingLabel,
          selectedProgramId ?? undefined
        );
        setExerciseProgress(data);
      } else if (selectedProgramDay !== null) {
        const data = await ExerciseService.getWorkoutProgress(selectedProgramDay, dateFrom, dateTo, nextGymId ?? undefined, selectedTrainingLabel, selectedProgramId ?? undefined);
        setWorkoutProgress(data);
      } else {
        setWorkoutProgress(null);
      }
    } catch (error) {
      console.error('Failed to reload progress data for gym filter:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTrainingLabelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as TrainingLabel | '';
    const nextTrainingLabel = value === '' ? null : value;
    setSelectedTrainingLabel(nextTrainingLabel);

    if (progressTab !== 'strength') {
      return;
    }

    setLoading(true);
    try {
      if (selectedMuscleGroup) {
        const data = await ExerciseService.getWorkoutProgressByMuscleGroup(selectedMuscleGroup, dateFrom, dateTo, selectedGymId ?? undefined, nextTrainingLabel, selectedProgramId ?? undefined);
        setWorkoutProgress(data);
      } else if (viewMode === 'exercise' && selectedExerciseId) {
        const data = await ExerciseService.getExerciseProgress(
          selectedExerciseId,
          selectedProgramDay ?? undefined,
          undefined,
          dateFrom,
          dateTo,
          selectedGymId ?? undefined,
          nextTrainingLabel,
          selectedProgramId ?? undefined
        );
        setExerciseProgress(data);
      } else if (selectedProgramDay !== null) {
        const data = await ExerciseService.getWorkoutProgress(selectedProgramDay, dateFrom, dateTo, selectedGymId ?? undefined, nextTrainingLabel, selectedProgramId ?? undefined);
        setWorkoutProgress(data);
      } else {
        setWorkoutProgress(null);
      }
    } catch (error) {
      console.error('Failed to reload progress data for training label filter:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshGroups = useCallback(async () => {
    try {
      const list = await ExerciseGroupService.listGroups();
      setGroups(list);
      return list;
    } catch (error) {
      console.error('Failed to load exercise groups:', error);
      return [];
    }
  }, []);

  const handleGroupChange = useCallback((groupId: number | null) => {
    if (groupId === null) {
      setSelectedGroupId(null);
      setGroupAnalysis(null);
      setViewMode('all');
      return;
    }
    setSelectedGroupId(groupId);
    setViewMode('group');
  }, []);

  // Fetch group analysis whenever the selected group or date range changes.
  useEffect(() => {
    if (progressTab !== 'strength' || viewMode !== 'group' || selectedGroupId === null) {
      return;
    }

    let isActive = true;
    setGroupAnalysisLoading(true);
    ExerciseGroupService.getAnalysis(selectedGroupId, { dateFrom, dateTo })
      .then((analysis) => {
        if (isActive) {
          setGroupAnalysis(analysis);
        }
      })
      .catch((error) => {
        console.error('Failed to load group analysis:', error);
        if (isActive) {
          setGroupAnalysis(null);
        }
      })
      .finally(() => {
        if (isActive) {
          setGroupAnalysisLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [progressTab, viewMode, selectedGroupId, dateFrom, dateTo]);

  useEffect(() => {
    if (progressTab !== 'strength') {
      return;
    }

    let isActive = true;
    setExerciseStatsLoading(true);

    ExerciseService.getExerciseStats({
      day: selectedProgramDay ?? undefined,
      dateFrom,
      dateTo,
      gymId: selectedGymId ?? undefined,
      programId: selectedProgramId ?? undefined,
      exerciseId: selectedExerciseId ?? undefined,
      muscleGroup: selectedMuscleGroup ?? undefined,
      includeVariations: false,
      trainingLabel: selectedTrainingLabel,
    })
      .then((response) => {
        if (!isActive) {
          return;
        }
        setExerciseStats(response.stats ?? []);
      })
      .catch((error) => {
        console.error('Failed to load exercise stats:', error);
        if (isActive) {
          setExerciseStats([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setExerciseStatsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    progressTab,
    selectedProgramDay,
    dateFrom,
    dateTo,
    selectedGymId,
    selectedProgramId,
    selectedExerciseId,
    selectedMuscleGroup,
    selectedTrainingLabel,
  ]);

  useEffect(() => {
    if (progressTab !== 'strength' || viewMode !== 'exercise' || !selectedExerciseId) {
      setExerciseRecords(null);
      setExerciseRecordsError(null);
      setExerciseRecordsLoading(false);
      return;
    }

    void refreshSelectedExerciseData();
  }, [progressTab, viewMode, selectedExerciseId, refreshSelectedExerciseData]);

  useEffect(() => {
    const handleWorkoutSessionUpdated = () => {
      void reloadCurrentProgressView();
    };

    window.addEventListener(WORKOUT_SESSION_UPDATED_EVENT, handleWorkoutSessionUpdated);

    return () => {
      window.removeEventListener(WORKOUT_SESSION_UPDATED_EVENT, handleWorkoutSessionUpdated);
    };
  }, [reloadCurrentProgressView]);

  // Chart data
  const allWorkoutsChartData = useMemo((): ChartDataPoint[] => {
    if (viewMode !== 'all' || !workoutProgress?.workouts) return [];
    return workoutProgress.workouts.map((workout) => {
      let totalVolume = 0, maxWeight = 0, totalReps = 0, totalRPE = 0, rpeCount = 0, setCount = 0;
      workout.exercises.forEach((exercise: any) => {
        exercise.sets.forEach((set: any) => {
          totalVolume += set.reps * set.weight;
          maxWeight = Math.max(maxWeight, set.weight);
          totalReps += set.reps;
          setCount++;
          if (set.rpe !== null) { totalRPE += set.rpe; rpeCount++; }
        });
      });
      return {
        workout: workout.name,
        date: new Date(workout.date).toLocaleDateString('bg-BG'),
        totalVolume, maxWeight,
        avgReps: setCount > 0 ? Math.round(totalReps / setCount) : 0,
        avgRPE: rpeCount > 0 ? Math.round((totalRPE / rpeCount) * 10) / 10 : null,
      };
    });
  }, [workoutProgress, viewMode]);

  const exerciseChartData = useMemo((): ChartDataPoint[] => {
    if (viewMode !== 'exercise' || !exerciseProgress?.workouts) return [];
    return exerciseProgress.workouts.map((workout) => {
      let totalVolume = 0, maxWeight = 0, totalReps = 0, totalRPE = 0, rpeCount = 0, setCount = 0;
      workout.sets.forEach((set: any) => {
        totalVolume += set.reps * set.weight;
        maxWeight = Math.max(maxWeight, set.weight);
        totalReps += set.reps;
        setCount++;
        if (set.rpe !== null) { totalRPE += set.rpe; rpeCount++; }
      });
      return {
        workout: workout.workoutName,
        date: new Date(workout.date).toLocaleDateString('bg-BG'),
        totalVolume, maxWeight,
        avgReps: setCount > 0 ? Math.round(totalReps / setCount) : 0,
        avgRPE: rpeCount > 0 ? Math.round((totalRPE / rpeCount) * 10) / 10 : null,
      };
    });
  }, [exerciseProgress, viewMode]);

  const chartData = viewMode === 'all' ? allWorkoutsChartData : exerciseChartData;

  const calculateImprovement = (data: ChartDataPoint[], metric: keyof ChartDataPoint): string => {
    if (data.length < 2) return 'N/A';
    const firstValue = data[0][metric];
    const lastValue = data[data.length - 1][metric];
    if (typeof firstValue !== 'number' || typeof lastValue !== 'number' || firstValue === 0) return 'N/A';
    const improvement = ((lastValue - firstValue) / firstValue) * 100;
    const sign = improvement > 0 ? '+' : '';
    return `${sign}${improvement.toFixed(1)}%`;
  };

  // Muscle visualization data
  const programDaysForVisualization = useMemo((): { [day: number]: ProgramExercise[] } => {
    const result: { [day: number]: ProgramExercise[] } = {};
    if (viewMode === 'all' && workoutProgress?.workouts) {
      workoutProgress.workouts.forEach(workout => {
        const dayNumber = workout.programDayNumber ?? 1;
        if (!result[dayNumber]) result[dayNumber] = [];
        workout.exercises.forEach((exerciseData: any) => {
          const exerciseOption = exerciseOptions.find(e => e.id === exerciseData.exerciseId);
          result[dayNumber].push({
            id: exerciseData.exerciseId,
            exercise: {
              id: exerciseData.exerciseId, name: exerciseData.exerciseName, description: '', category: '',
              equipment: '', muscleGroup: exerciseOption?.muscleGroup || '',
              primaryMuscleGroup: exerciseOption?.primaryMuscleGroup ?? exerciseOption?.muscleGroup,
              secondaryMuscleGroup: exerciseOption?.secondaryMuscleGroup ?? undefined,
              tertiaryMuscleGroup: exerciseOption?.tertiaryMuscleGroup ?? undefined,
              difficulty: '', createdAt: new Date().toISOString(),
            },
            dayOfWeek: dayNumber, orderInSession: 0, createdAt: new Date().toISOString(),
          });
        });
      });
    } else if (viewMode === 'exercise' && exerciseProgress?.workouts) {
      exerciseProgress.workouts.forEach((workout: any, index: number) => {
        const dayNumber = index + 1;
        if (!result[dayNumber]) result[dayNumber] = [];
        const exerciseOption = exerciseOptions.find(e => e.id === selectedExerciseId);
        if (exerciseOption) {
          result[dayNumber].push({
            id: selectedExerciseId || 0,
            exercise: {
              id: selectedExerciseId || 0, name: exerciseOption.name, description: '', category: '',
              equipment: '', muscleGroup: exerciseOption.muscleGroup,
              primaryMuscleGroup: exerciseOption.primaryMuscleGroup ?? exerciseOption.muscleGroup,
              secondaryMuscleGroup: exerciseOption.secondaryMuscleGroup ?? undefined,
              tertiaryMuscleGroup: exerciseOption.tertiaryMuscleGroup ?? undefined,
              difficulty: '', createdAt: new Date().toISOString(),
            },
            dayOfWeek: dayNumber, orderInSession: 0, createdAt: new Date().toISOString(),
          });
        }
      });
    }
    return result;
  }, [workoutProgress, exerciseProgress, viewMode, selectedExerciseId, exerciseOptions]);

  const workoutSessionsForVisualization = useMemo(() => {
    if (viewMode === 'exercise' && exerciseProgress?.workouts) {
      const exerciseOption = exerciseOptions.find(e => e.id === selectedExerciseId);
      return exerciseProgress.workouts.map((workout) => ({
        id: workout.sessionId, name: workout.workoutName, date: workout.date,
        programDayNumber: workout.programDayNumber,
        exercises: [{
          exerciseId: selectedExerciseId || 0, exerciseName: exerciseOption?.name || '',
          sets: workout.sets, muscleGroup: exerciseOption?.muscleGroup,
          primaryMuscleGroup: exerciseOption?.primaryMuscleGroup,
          secondaryMuscleGroup: exerciseOption?.secondaryMuscleGroup,
          tertiaryMuscleGroup: exerciseOption?.tertiaryMuscleGroup,
        }],
      }));
    } else if (workoutProgress?.workouts) {
      return workoutProgress.workouts;
    }
    return [];
  }, [viewMode, exerciseProgress, workoutProgress, selectedExerciseId, exerciseOptions]);

  // Last execution for print table
  const lastExecution = useMemo((): LastExecutionRow[] | null => {
    const rows: LastExecutionRow[] = [];
    if (viewMode === 'exercise' && exerciseProgress?.workouts && exerciseProgress.workouts.length > 0) {
      const lastWorkout = exerciseProgress.workouts[exerciseProgress.workouts.length - 1];
      const exerciseOption = exerciseOptions.find(e => e.id === selectedExerciseId);
      let maxW = 0, totalR = 0, rpeSum = 0, rpeCount = 0;
      lastWorkout.sets.forEach((set: any) => {
        maxW = Math.max(maxW, set.weight); totalR += set.reps;
        if (set.rpe !== null) { rpeSum += set.rpe; rpeCount++; }
      });
      rows.push({ exercise: exerciseOption?.name || '', sets: lastWorkout.sets.length, maxWeight: maxW, totalReps: totalR, avgRPE: rpeCount > 0 ? (rpeSum / rpeCount).toFixed(1) : '-' });
    } else if (viewMode === 'all' && workoutProgress?.workouts && workoutProgress.workouts.length > 0) {
      const lastWorkout = workoutProgress.workouts[workoutProgress.workouts.length - 1];
      lastWorkout.exercises.forEach((ex: any) => {
        let maxW = 0, totalR = 0, rpeSum = 0, rpeCount = 0;
        ex.sets.forEach((set: any) => {
          maxW = Math.max(maxW, set.weight); totalR += set.reps;
          if (set.rpe !== null) { rpeSum += set.rpe; rpeCount++; }
        });
        rows.push({ exercise: ex.exerciseName, sets: ex.sets.length, maxWeight: maxW, totalReps: totalR, avgRPE: rpeCount > 0 ? (rpeSum / rpeCount).toFixed(1) : '-' });
      });
    }
    return rows.length > 0 ? rows : null;
  }, [viewMode, exerciseProgress, workoutProgress, selectedExerciseId, exerciseOptions]);

  // Last session detail for on-screen display
  const lastSessionDetail = useMemo((): LastSessionDetail | null => {
    if (viewMode === 'exercise' && exerciseProgress?.workouts && exerciseProgress.workouts.length > 0) {
      const lastWorkout = exerciseProgress.workouts[exerciseProgress.workouts.length - 1];
      const exerciseOption = exerciseOptions.find(e => e.id === selectedExerciseId);
      return { date: lastWorkout.date, name: lastWorkout.workoutName, exercises: [{ exerciseName: exerciseOption?.name || '', sets: lastWorkout.sets }] };
    } else if (viewMode === 'all' && workoutProgress?.workouts && workoutProgress.workouts.length > 0) {
      const lastWorkout = workoutProgress.workouts[workoutProgress.workouts.length - 1];
      return { date: lastWorkout.date, name: lastWorkout.name, exercises: lastWorkout.exercises.map((ex) => ({ exerciseName: ex.exerciseName, sets: ex.sets })) };
    }
    return null;
  }, [viewMode, exerciseProgress, workoutProgress, selectedExerciseId, exerciseOptions]);

  // Print muscle load
  const { printMuscleValues, printMuscleWeight } = useMemo(() => {
    const load: Record<string, number> = {};
    const weight: Record<string, number> = {};
    Object.values(programDaysForVisualization).forEach(exercises => {
      exercises.forEach(pe => {
        const primary = pe.exercise.primaryMuscleGroup || pe.exercise.muscleGroup;
        if (primary && primary !== 'cardio') load[primary] = (load[primary] || 0) + 1.0;
        if (pe.exercise.secondaryMuscleGroup && pe.exercise.secondaryMuscleGroup !== 'cardio') load[pe.exercise.secondaryMuscleGroup] = (load[pe.exercise.secondaryMuscleGroup] || 0) + 0.5;
        if (pe.exercise.tertiaryMuscleGroup && pe.exercise.tertiaryMuscleGroup !== 'cardio') load[pe.exercise.tertiaryMuscleGroup] = (load[pe.exercise.tertiaryMuscleGroup] || 0) + 0.25;
      });
    });
    const workoutsByDay = new Map<number, typeof workoutSessionsForVisualization[0]>();
    workoutSessionsForVisualization.forEach(ws => {
      if (ws.programDayNumber !== null) {
        const existing = workoutsByDay.get(ws.programDayNumber);
        if (!existing || new Date(ws.date) > new Date(existing.date)) workoutsByDay.set(ws.programDayNumber, ws);
      }
    });
    workoutsByDay.forEach(workout => {
      workout.exercises.forEach((ex: any) => {
        const totalVolume = ex.sets.reduce((sum: number, set: any) => sum + (set.reps * set.weight), 0);
        const primary = ex.primaryMuscleGroup || ex.muscleGroup;
        if (primary && primary !== 'cardio') weight[primary] = (weight[primary] || 0) + totalVolume;
        if (ex.secondaryMuscleGroup && ex.secondaryMuscleGroup !== 'cardio') weight[ex.secondaryMuscleGroup] = (weight[ex.secondaryMuscleGroup] || 0) + totalVolume * 0.5;
        if (ex.tertiaryMuscleGroup && ex.tertiaryMuscleGroup !== 'cardio') weight[ex.tertiaryMuscleGroup] = (weight[ex.tertiaryMuscleGroup] || 0) + totalVolume * 0.25;
      });
    });
    Object.keys(load).forEach(k => { load[k] = Math.round(load[k] * 10) / 10; });
    return { printMuscleValues: load, printMuscleWeight: weight };
  }, [programDaysForVisualization, workoutSessionsForVisualization]);

  const currentWorkouts = viewMode === 'exercise'
    ? exerciseProgress?.workouts ?? []
    : workoutProgress?.workouts ?? [];

  return {
    // Tab
    progressTab, setProgressTab,
    // State
    loading, viewMode, workoutProgress, exerciseProgress, cardioProgress,
    exerciseOptions, selectedExerciseId, selectedProgramDay, availableProgramDays,
    selectedProgramId, programs, selectedVariation, selectedMuscleGroup, selectedTrainingLabel, selectedGymId, gyms, successRates, dateFrom, dateTo,
    exerciseStats, exerciseStatsLoading,
    exerciseRecords, setExerciseRecords, exerciseRecordsLoading, exerciseRecordsError,
    // Exercise groups
    groups, selectedGroupId, groupAnalysis, groupAnalysisLoading,
    refreshGroups, handleGroupChange,
    // Computed
    groupedExerciseOptions, variationOptions, chartData, currentWorkouts,
    lastExecution, lastSessionDetail,
    programDaysForVisualization, workoutSessionsForVisualization,
    printMuscleValues, printMuscleWeight,
    // Functions
    calculateImprovement, handleProgramChange, handleExerciseChange, handleProgramDayChange,
    handleVariationChange, handleMuscleGroupChange, handleTrainingLabelChange, handleGymChange, handleDateChange,
    refreshSelectedExerciseData,
  };
}
